import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const [readingChapter, setReadingChapter] = useState<{ id: string; title: string; number: number } | null>(null);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title}_免费在线阅读_89890漫画网`;
    } else {
      document.title = '漫画详情_89890漫画网';
    }

    const existing = document.getElementById('schema-comic');
    if (existing) existing.remove();
    if (comic) {
      const script = document.createElement('script');
      script.id = 'schema-comic';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: comic.title,
        description: comic.description,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.chapters[0]?.updateTime,
        dateModified: comic.updateTime,
        url: `https://www.89890.com/comic/${comic.id}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.89890.com' },
            { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `https://www.89890.com/category/${comic.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: comic.title, item: `https://www.89890.com/comic/${comic.id}` },
          ],
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById('schema-comic');
      if (s) s.remove();
    };
  }, [comic, id]);

  const handleReadChapter = (chapter: { id: string; title: string; number: number }) => {
    setReadingChapter(chapter);
  };

  const closeReader = () => {
    setReadingChapter(null);
  };

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900">漫画未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        {readingChapter && (
          <div className="fixed inset-0 z-50 bg-background-950 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 bg-background-900 border-b border-background-700/50">
              <button
                onClick={closeReader}
                className="flex items-center gap-2 text-sm text-foreground-300 hover:text-background-50 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回详情
              </button>
              <h2 className="text-sm font-medium text-background-50 font-heading">{readingChapter.title}</h2>
              <div className="flex items-center gap-3">
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-background-50 hover:bg-background-700/50 transition-colors cursor-pointer"
                  disabled={readingChapter.number <= 1}
                  onClick={() => {
                    const prev = comic.chapters.find((c) => c.number === readingChapter.number - 1);
                    if (prev) handleReadChapter(prev);
                  }}
                >
                  <i className="ri-arrow-up-s-line"></i>
                </button>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-background-50 hover:bg-background-700/50 transition-colors cursor-pointer"
                  disabled={readingChapter.number >= comic.chapters.length}
                  onClick={() => {
                    const next = comic.chapters.find((c) => c.number === readingChapter.number + 1);
                    if (next) handleReadChapter(next);
                  }}
                >
                  <i className="ri-arrow-down-s-line"></i>
                </button>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-lg">
                <div className="w-24 h-24 mx-auto rounded-2xl bg-background-800 flex items-center justify-center">
                  <i className="ri-book-open-line text-4xl text-primary-400"></i>
                </div>
                <h3 className="mt-6 text-xl font-heading text-background-50 font-bold">{readingChapter.title}</h3>
                <p className="mt-3 text-sm text-foreground-400 leading-relaxed">
                  漫画章节阅读区 —— {comic.title}<br />
                  当前阅读：{readingChapter.title}<br />
                  {comic.chapters.length}话全集中，第{readingChapter.number}话
                </p>
                <p className="mt-4 text-xs text-foreground-500">
                  {comic.description}
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <button
                    className="px-4 py-2 text-sm bg-background-800 text-foreground-300 rounded-lg hover:bg-background-700 transition-colors cursor-pointer whitespace-nowrap"
                    disabled={readingChapter.number <= 1}
                    onClick={() => {
                      const prev = comic.chapters.find((c) => c.number === readingChapter.number - 1);
                      if (prev) handleReadChapter(prev);
                    }}
                  >
                    上一话
                  </button>
                  <span className="text-sm text-foreground-400">
                    {readingChapter.number} / {comic.chapters.length}
                  </span>
                  <button
                    className="px-4 py-2 text-sm bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    disabled={readingChapter.number >= comic.chapters.length}
                    onClick={() => {
                      const next = comic.chapters.find((c) => c.number === readingChapter.number + 1);
                      if (next) handleReadChapter(next);
                    }}
                  >
                    下一话
                  </button>
                </div>
                {readingChapter.number === comic.chapters.length && (
                  <div className="mt-4 px-4 py-2 bg-accent-100 text-accent-800 rounded-lg text-sm font-medium">
                    你已到达大结局！感谢阅读
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{comic.category}漫画</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium whitespace-nowrap">{comic.title}</span>
            </nav>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="lg:w-[300px] shrink-0">
              <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: '3/4' }}>
                <img
                  src={comic.coverImage}
                  alt={`${comic.title}封面`}
                  title={`${comic.title} - 免费在线阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                  {comic.category}
                </span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                  comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 font-bold">{comic.title}</h1>
              <p className="mt-2 text-sm text-foreground-500">作者：{comic.author}</p>

              <div className="mt-5 flex items-center gap-5 text-sm">
                <span className="flex items-center gap-1.5 text-accent-500 font-semibold">
                  <i className="ri-star-fill"></i>
                  {comic.rating} 分
                </span>
                <span className="text-foreground-400 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(comic.views / 10000).toFixed(0)} 万人阅读
                </span>
                <span className="text-foreground-400">共 {comic.chapters.length} 话</span>
              </div>

              <div className="mt-6 p-5 bg-background-100 rounded-2xl">
                <h2 className="text-sm font-semibold text-foreground-700 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => handleReadChapter(comic.chapters[0])}
                  className="flex-1 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </button>
                {comic.status === '已完结' && (
                  <button
                    onClick={() => handleReadChapter(comic.chapters[comic.chapters.length - 1])}
                    className="flex-1 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-flag-line text-lg"></i>
                    直接看大结局
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-12 md:mt-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 font-bold mb-6">章节列表</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {comic.chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => handleReadChapter(chapter)}
                  className={`flex items-center justify-between px-5 py-4 rounded-xl border transition-all cursor-pointer text-left ${
                    chapter.number === comic.chapters.length
                      ? 'border-accent-300 bg-accent-50 hover:bg-accent-100'
                      : 'border-background-200/70 bg-background-50 hover:border-primary-200 hover:bg-primary-50/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-background-200 text-foreground-600 text-xs font-semibold flex items-center justify-center shrink-0">
                      {chapter.number}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{chapter.title}</p>
                      <p className="text-xs text-foreground-400 mt-0.5">{chapter.updateTime}</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto pb-14 md:pb-20">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 font-bold mb-6">相关推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {comics
              .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
              .slice(0, 5)
              .map((related) => (
                <Link
                  key={related.id}
                  to={`/comic/${related.id}`}
                  className="group cursor-pointer"
                  data-product-shop=""
                >
                  <div className="rounded-xl overflow-hidden" style={{ aspectRatio: '3/4' }}>
                    <img
                      src={related.coverImage}
                      alt={related.title}
                      title={`${related.title} - 免费在线阅读`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="mt-2.5 text-sm font-heading text-foreground-800 font-medium group-hover:text-primary-500 transition-colors line-clamp-1">
                    {related.title}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-0.5">{related.author}</p>
                </Link>
              ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}